import React, { useState, useEffect } from 'react'
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Link from "@mui/material/Link";
import { useNavigate, useParams } from 'react-router-dom';
import { getListById, getUserById, removeProfileFromList, getTeamById } from '../../services/userService';
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import ProfileView from '../profileDetails/ProfileView';

const TeamsListsDetails = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [emailIds, setEmailIds] = useState('');

  useEffect(() => {
    const fetchTeamDetails = async () => {
      try {
        setLoading(true);
        const response = await getTeamById(teamId);
        console.log('List response:', response);
        if (response.status === 'success') {
          setTeam(response);
          // Use profiles data from backend response
          if (response.users && response.users.length > 0) {
            setProfiles(response.users);
          } else {
            setProfiles([]);
          }
        }
      } catch (error) {
        console.error('Error fetching list details:', error);
        setError('Failed to load list details');
      } finally {
        setLoading(false);
      }
    };

    if (teamId) {
      fetchTeamDetails();
    }
  }, [teamId]);

  const handleDeleteProfile = async (linkedinUsername) => {
    try {
      // Optimistically update the UI
      const updatedProfiles = profiles.filter(profile => profile.linkedinUsername !== linkedinUsername);
      setProfiles(updatedProfiles);
      
      // Call API to remove profile from team in backend
      const response = await removeProfileFromList(teamId, linkedinUsername);
      if (response.status === 'success') {
        console.log(`Profile ${linkedinUsername} removed from list successfully`);
      } else {
        // If API call fails, revert the UI change
        setProfiles(profiles);
        console.error('Failed to remove profile from list');
      }
    } catch (error) {
      // If API call fails, revert the UI change
      setProfiles(profiles);
      console.error('Error removing profile:', error);
    }
  };

  // Profile click handler
  const handleProfileClick = (profile) => {
    setSelectedProfile(profile);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
  };

  const handleShare = () => {
    setShareDialogOpen(true);
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    setEmailIds('');
  };

  const handleEmailChange = (event) => {
    setEmailIds(event.target.value);
  };

  const handleSendShare = () => {
    // Validate email format
    const emailArray = emailIds.split(',').map(email => email.trim()).filter(email => email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emailArray.filter(email => !emailRegex.test(email));
    
    if (invalidEmails.length > 0) {
      alert('Please enter valid email addresses');
      return;
    }
    
    if (emailArray.length === 0) {
      alert('Please enter at least one email address');
      return;
    }
    
    // Here you would typically send the share request to your backend
    console.log('Sharing team with emails:', emailArray);
    console.log('Team URL:', window.location.href);
    
    // For now, just show success message and close dialog
    alert(`Team shared successfully with ${emailArray.length} email(s)!`);
    handleShareDialogClose();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendShare();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!team) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="warning">List not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: '#666' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
          {team.name}
        </Typography>
        <IconButton onClick={handleShare} sx={{ color: '#666', ml: 1 }}>
          <ShareIcon />
        </IconButton>
      </Box>
      
      {team.description && (
        <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
            {team.description}
        </Typography>
      )} 
      
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
        Profiles ({profiles.length})
      </Typography>
      
      {profiles.length === 0 ? (
        <Alert severity="info">No profiles found in this team</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Picture</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Social Footprint</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Position</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>company</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profiles.map((profile, index) => (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      <img src={profile.profilePicture} alt="Profile" style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : profile.linkedinLink ? (
                      <IconButton 
                        size={'large'} 
                        onClick={() => {
                          const url = profile.linkedinLink.startsWith('http') 
                            ? profile.linkedinLink 
                            : `https://${profile.linkedinLink}`;
                          window.open(url, '_blank');
                        }}
                        sx={{ '&:hover': { backgroundColor: 'rgba(0, 119, 181, 0.1)' } }}
                      >
                        <LinkedInIcon sx={{ fontSize: '25px', color: '#0077b5' }} />
                      </IconButton>
                    ) : (
                      <Typography color="text.secondary">No link</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      <Typography 
                        sx={{ 
                          cursor: 'pointer', 
                          color: '#1976d2', 
                          textDecoration: 'underline',
                          '&:hover': { color: '#1565c0' }
                        }}
                        onClick={() => handleProfileClick(profile)}
                      >
                        {profile.fullName || 'N/A'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      profile.currentPosition || 'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      profile.company || 'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    {profile.error ? (
                      <Typography color="error">Not found</Typography>
                    ) : (
                      profile.location || 'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDeleteProfile(profile.linkedinUsername)} sx={{ color: '#666' }}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Profile Detail Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth={false} PaperProps={{sx: { width: '60vw', height: '80vh', marginTop: '64px', marginLeft: '240px', marginRight: '240px', borderRadius: 2, }}}>
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ width: '100%', height: '100%', p: 2 }}>
          {selectedProfile && (
            <ProfileView profileData={selectedProfile} />
          )}
        </Box>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={handleShareDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Share Team
          <IconButton
            aria-label="close"
            onClick={handleShareDialogClose}
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Share "{team.name}" with others by entering their email addresses below.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Email Addresses"
            placeholder="Enter email addresses separated by commas"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={emailIds}
            onChange={handleEmailChange}
            onKeyPress={handleKeyPress}
            helperText="Separate multiple email addresses with commas. Press Enter to send."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleShareDialogClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSendShare} variant="contained" color="primary">
            Send Share
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TeamsListsDetails
